import { readStore, writeStore, type WorkflowsStore } from './workflowsStorage';
import type { WorkflowDefinition, Workflow, DrainingVersion } from '~/types/workflow.types';
import type {
    CreateWorkflowInput,
    ListWorkflowsParams,
    UpdateDraftInput,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import { AxiosError, AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

// The localStorage fake fabricates axios envelopes and axios-style errors so it is a
// drop-in for the real HTTP client: callers read response.data and branch on
// error.response?.status exactly as they will against the backend.
const fakeConfig = (): InternalAxiosRequestConfig => ({ headers: new AxiosHeaders() });

const ok = <T>(data: T, status = 200): AxiosResponse<T> => ({
    data,
    status,
    statusText: 'OK',
    headers: {},
    config: fakeConfig(),
});

const fail = (status: number, message: string, data?: unknown): AxiosError => {
    const config = fakeConfig();
    const response: AxiosResponse = {
        data: data ?? { message },
        status,
        statusText: message,
        headers: {},
        config,
    };

    return new AxiosError(message, String(status), config, undefined, response);
};

const generateId = (prefix: string): string => `${prefix}_${crypto.randomUUID()}`;

const latestVersion = (versions: Workflow[]): Workflow | undefined => {
    const sorted = [...versions].sort((a, b) => a.version - b.version);

    return sorted[sorted.length - 1];
};

// The version a browse list should surface: the live (latest published) version, or the
// latest draft for workflows never published. Keeps an in-progress draft from replacing
// the published row a contact would actually receive.
const displayVersion = (versions: Workflow[]): Workflow | undefined => {
    const published = versions.filter((flow) => flow.status === 'published');

    return latestVersion(published.length > 0 ? published : versions);
};

export const getWorkflowDependencies = (definition: WorkflowDefinition): string[] => {
    const ids = new Set<string>();

    for (const step of definition.steps) {
        const target = step.target_workflow_id;
        if (step.type === 'send_to_workflow' && typeof target === 'string' && target !== '') {
            ids.add(target);
        }
    }

    return [...ids];
};

// Minimal publish-time validation; AUDI-205 will expand the rule set. Node-internal edges
// (e.g. condition branch targets) are validated by their node components, not here.
export const validateDefinition = (definition: WorkflowDefinition): string[] => {
    const issues: string[] = [];

    if (definition.name.trim() === '') {
        issues.push('Workflow name is required.');
    }

    if (definition.steps.length === 0) {
        issues.push('Workflow must contain at least one step.');
    }

    const stepIds = new Set(definition.steps.map((step) => step.step_id));
    for (const step of definition.steps) {
        if (step.next_step !== undefined && !stepIds.has(step.next_step)) {
            issues.push(`Step "${step.step_id}" points to unknown step "${step.next_step}".`);
        }
    }

    return issues;
};

// Returned objects are cloned so they never alias the store, matching a real HTTP client's
// fresh-deserialized semantics. (Methods are async only to honor the interface.)
export default class LocalStorageWorkflowsClient implements WorkflowsClientInterface {
    async getWorkflows(params?: ListWorkflowsParams): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();
        const archived = params?.archived ?? false;

        const workflows = Object.values(store.workflows)
            .map((versions) => this.displayRow(versions))
            .filter((flow): flow is Workflow => flow !== undefined)
            .filter((flow) => archived === (flow.deleted_at !== null));

        return ok(workflows);
    }

    async getWorkflow(workflowId: string, version?: number): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        if (version !== undefined) {
            // Explicit-version lookups intentionally return archived versions: history
            // and version-detail pages inspect them.
            const match = versions.find((flow) => flow.version === version);
            if (match === undefined) {
                throw fail(404, `Version ${version} of workflow "${workflowId}" was not found.`);
            }

            return ok(structuredClone(match));
        }

        const latest = latestVersion(versions.filter((flow) => flow.deleted_at === null));
        if (latest === undefined) {
            throw fail(404, `Workflow "${workflowId}" was not found.`);
        }

        return ok(structuredClone(latest));
    }

    async getDependentWorkflows(workflowId: string): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();

        const dependents = Object.values(store.workflows)
            .map((versions) => this.displayRow(versions))
            .filter((workflow): workflow is Workflow => workflow !== undefined)
            .filter((workflow) => workflow.deleted_at === null)
            .filter((workflow) => workflow.workflow_id !== workflowId)
            .filter((workflow) => getWorkflowDependencies(workflow.definition).includes(workflowId));

        return ok(dependents.map((workflow) => structuredClone(workflow)));
    }

    async createWorkflow(input: CreateWorkflowInput): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const workflow: Workflow = {
            workflow_id: generateId('workflow'),
            version: 1,
            status: 'draft',
            published_at: null,
            deleted_at: null,
            updated_at: new Date().toISOString(),
            definition: { name: input.name, description: input.description ?? '', steps: [] },
        };

        store.workflows[workflow.workflow_id] = [workflow];
        this.save(store);

        return ok(structuredClone(workflow), 201);
    }

    async createDraft(workflowId: string): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);
        const latest = latestVersion(versions);

        if (latest === undefined || latest.deleted_at !== null) {
            throw fail(409, `Workflow "${workflowId}" is archived.`);
        }

        // A draft is already open — editing it directly is the right behavior, so this is
        // idempotent rather than an error.
        if (latest.status === 'draft') {
            return ok(structuredClone(latest));
        }

        const draft: Workflow = {
            workflow_id: workflowId,
            version: latest.version + 1,
            status: 'draft',
            published_at: null,
            deleted_at: null,
            updated_at: new Date().toISOString(),
            definition: structuredClone(latest.definition),
        };

        versions.push(draft);
        this.save(store);

        return ok(structuredClone(draft), 201);
    }

    async updateDraft(workflowId: string, input: UpdateDraftInput): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const draft = this.requireEditableDraft(store, workflowId);

        draft.definition = structuredClone(input.definition);
        draft.updated_at = new Date().toISOString();
        this.save(store);

        return ok(structuredClone(draft));
    }

    async publishWorkflow(workflowId: string): Promise<AxiosResponse<Workflow>> {
        const store = readStore();
        const draft = this.requireEditableDraft(store, workflowId);

        const issues = validateDefinition(draft.definition);
        if (issues.length > 0) {
            throw fail(422, 'Workflow definition is invalid.', {
                message: 'Workflow definition is invalid.',
                errors: issues,
            });
        }

        const now = new Date().toISOString();
        draft.status = 'published';
        draft.published_at = now;
        draft.updated_at = now;
        this.save(store);

        return ok(structuredClone(draft));
    }

    async archiveWorkflow(workflowId: string): Promise<AxiosResponse<void>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);
        const now = new Date().toISOString();

        for (const flow of versions) {
            flow.deleted_at = now;
        }
        this.save(store);

        return ok<void>(undefined);
    }

    async restoreWorkflow(workflowId: string): Promise<AxiosResponse<void>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        for (const flow of versions) {
            flow.deleted_at = null;
        }
        this.save(store);

        return ok<void>(undefined);
    }

    // Full version history for one workflow (every record, newest first) — the History/detail
    // view's data source, not the list grid's.
    async getWorkflowVersions(workflowId: string): Promise<AxiosResponse<Workflow[]>> {
        const store = readStore();
        const versions = this.requireVersions(store, workflowId);

        return ok([...versions].sort((a, b) => b.version - a.version).map((flow) => structuredClone(flow)));
    }

    // Runtime state: older published versions still draining active instances. A different
    // source from the metadata above, so each card fetches it on its own and can refresh it.
    async getDrainingVersions(workflowId: string): Promise<AxiosResponse<DrainingVersion[]>> {
        const store = readStore();

        return ok(structuredClone(store.drainingVersions[workflowId] ?? []));
    }

    // The display row (live published, else latest draft) plus the parent's denormalized
    // published/draft version pointers the list surfaces for badges.
    private displayRow(versions: Workflow[]): Workflow | undefined {
        const display = displayVersion(versions);
        if (display === undefined) {
            return undefined;
        }

        const published = versions.filter((flow) => flow.status === 'published');

        return {
            ...structuredClone(display),
            published_version: published.length > 0 ? Math.max(...published.map((flow) => flow.version)) : null,
            draft_version: versions.find((flow) => flow.status === 'draft')?.version ?? null,
        };
    }

    private requireVersions(store: WorkflowsStore, workflowId: string): Workflow[] {
        const versions = store.workflows[workflowId];
        if (versions === undefined || versions.length === 0) {
            throw fail(404, `Workflow "${workflowId}" was not found.`);
        }

        return versions;
    }

    private requireEditableDraft(store: WorkflowsStore, workflowId: string): Workflow {
        const versions = this.requireVersions(store, workflowId);
        const latest = latestVersion(versions);

        if (latest === undefined || latest.status !== 'draft') {
            throw fail(409, `Workflow "${workflowId}" has no open draft; create one before editing.`);
        }

        if (latest.deleted_at !== null) {
            throw fail(409, `Workflow "${workflowId}" is archived.`);
        }

        return latest;
    }

    private save(store: WorkflowsStore): void {
        try {
            writeStore(store);
        } catch {
            // Every call re-reads from localStorage, so a failed write silently loses the
            // mutation on the next operation — surface it as a 500 so the UI can warn.
            throw fail(500, 'The change could not be saved.');
        }
    }
}
